import { AfterViewInit, Component, ElementRef, inject, OnDestroy, viewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { merge, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  lastAlarm,
  lastAttributesState,
  lastBlind,
  lastButtonPress,
  lastCurtain,
  lastFanSpeed,
  lastLight,
  lastMediaState,
  lastMood,
  lastRoomState,
  lastTemperature,
} from '../../../stores/messages/messages.selector';
import { ToggleTerminal } from '../../../stores/ui/ui.actions';

@Component({
  selector: 'era-raw',
  templateUrl: './raw.component.html',
  styleUrls: ['./raw.component.scss'],
})
export class RawComponent implements AfterViewInit, OnDestroy {
  private readonly store = inject(Store);

  private readonly subscriptions: Subscription[] = [];
  private readonly consoleWritings: Observable<string>;

  readonly raw = viewChild.required<ElementRef>('raw');
  constructor() {
    const alarmConsole: Observable<string> = this.store.select(lastAlarm).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | ALARM | ${JSON.stringify(x)}`)
    );

    const blindConsole: Observable<string> = this.store.select(lastBlind).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | BLIND | ${JSON.stringify(x)}`)
    );

    const buttonConsole: Observable<string> = this.store.select(lastButtonPress).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | BUTTON | ${JSON.stringify(x)}`)
    );

    const curtainConsole: Observable<string> = this.store.select(lastCurtain).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | CURTAIN | ${JSON.stringify(x)}`)
    );

    const fanspeedConsole: Observable<string> = this.store.select(lastFanSpeed).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | FANSPEED | ${JSON.stringify(x)}`)
    );

    const lightConsole: Observable<string> = this.store.select(lastLight).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | LIGHT | ${JSON.stringify(x)}`)
    );

    const mediaConsole: Observable<string> = this.store.select(lastMediaState).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | MEDIA | ${JSON.stringify(x)}`)
    );

    const temperatureConsole: Observable<string> = this.store.select(lastTemperature).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | TEMPERATURE | ${JSON.stringify(x)}`)
    );

    const moodConsole: Observable<string> = this.store.select(lastMood).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | MOOD | ${JSON.stringify(x)}`)
    );

    const roomStateConsole: Observable<string> = this.store.select(lastRoomState).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | ROOM STATE | ${JSON.stringify(x)}`)
    );

    const attributesStateConsole: Observable<string> = this.store.select(lastAttributesState).pipe(
      filter((x) => x != null),
      map((x) => `${new Date().toLocaleString()} | ATTRIBUTES | ${JSON.stringify(x)}`)
    );

    this.consoleWritings = merge(
      alarmConsole,
      blindConsole,
      buttonConsole,
      fanspeedConsole,
      curtainConsole,
      lightConsole,
      mediaConsole,
      moodConsole,
      roomStateConsole,
      attributesStateConsole,
      temperatureConsole
    );
  }
  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.consoleWritings.subscribe((text) => {
        this.writeToConsole(text);
      })
    );
  }

  public close(): void {
    this.store.dispatch(new ToggleTerminal({ isOpen: false }));
  }

  private writeToConsole(message: string): void {
    const textNode = document.createTextNode(message);
    this.raw().nativeElement.appendChild(textNode);
    this.raw().nativeElement.appendChild(document.createElement('br'));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }
}
